import { FC, useCallback, useEffect, useRef } from "react";
import { useController, type Control } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormItem, FormLabel } from "@/components/ui/form";
import { cn } from "@/lib/utils";

interface Props {
  control: Control<any>;
  disabled?: boolean;
  publicTokenizationKey: string;
  onInit: (ctx: { getToken: () => Promise<string> }) => void;
}

const NaxellaFormFields: FC<Props> = ({
  control,
  disabled,
  publicTokenizationKey,
  onInit,
}) => {
  const { t } = useTranslation();
  const lastRequestIdRef = useRef<string | null>(null);
  const pendingResolveRef = useRef<((token: string) => void) | null>(null);
  const pendingRejectRef = useRef<((e: any) => void) | null>(null);

  const {
    field: numberField,
    fieldState: { error: numberError },
  } = useController({ control, name: "card_number", defaultValue: false });
  const {
    field: expiryField,
    fieldState: { error: expiryError },
  } = useController({ control, name: "card_expiry", defaultValue: false });
  const {
    field: cvcField,
    fieldState: { error: cvcError },
  } = useController({ control, name: "card_cvc", defaultValue: false });

  // Always-current handlers without adding to effect deps
  const fieldHandlersRef = useRef({
    ccnumber: numberField.onChange,
    ccexp: expiryField.onChange,
    cvv: cvcField.onChange,
  });
  fieldHandlersRef.current.ccnumber = numberField.onChange;
  fieldHandlersRef.current.ccexp = expiryField.onChange;
  fieldHandlersRef.current.cvv = cvcField.onChange;

  const hardReset = () => {
    try {
      document
        .querySelectorAll("iframe.CollectJSInlineIframe")
        .forEach((el) => el.remove());
      ["ccnumber", "ccexp", "cvv"].forEach((id) => {
        const node = document.getElementById(id);
        if (node) node.innerHTML = "";
      });
      if ((window as any).CollectJS?.iframes) {
        (window as any).CollectJS.iframes = {};
      }
    } catch {}
  };

  const getToken = useCallback((): Promise<string> => {
    return new Promise((resolve, reject) => {
      const requestId =
        Date.now().toString(36) + Math.random().toString(36).slice(2);
      lastRequestIdRef.current = requestId;
      pendingResolveRef.current = resolve;
      pendingRejectRef.current = reject;

      const evt = new CustomEvent("payment-request", { detail: { requestId } });
      (window as any).CollectJS.startPaymentRequest(evt);

      setTimeout(() => {
        if (pendingRejectRef.current) {
          pendingResolveRef.current = null;
          pendingRejectRef.current = null;
          lastRequestIdRef.current = null;
          reject(new Error("CollectJS tokenization timeout"));
        }
      }, 30000);
    });
  }, []);

  useEffect(() => {
    if (disabled) return;

    const isCollectMounted = () =>
      !!(window as any).CollectJS?.iframes?.cvv?.isConnected;

    if (isCollectMounted()) {
      onInit({ getToken });
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const src = "https://naxella.transactiongateway.com/token/Collect.js";
        await new Promise<void>((resolve, reject) => {
          if ((window as any).CollectJS) return resolve();
          const existing = document.querySelector(
            `script[src="${src}"]`,
          ) as HTMLScriptElement | null;
          if (existing) {
            existing.addEventListener("load", () => resolve(), { once: true });
            existing.addEventListener("error", reject, { once: true });
            return;
          }
          const script = document.createElement("script");
          script.src = src;
          script.async = true;
          script.setAttribute("data-tokenization-key", publicTokenizationKey);
          script.setAttribute("data-price", "0.00");
          script.setAttribute("data-country", "CA");
          script.setAttribute("data-currency", "CAD");
          script.setAttribute(
            "data-field-apple-pay-selector",
            "#applepay-sink",
          );
          script.onload = () => resolve();
          script.onerror = reject;
          document.body.appendChild(script);
        });

        if (cancelled) return;
        if (!(window as any).CollectJS)
          throw new Error("CollectJS failed to load");

        hardReset();

        (window as any).CollectJS.configure({
          paymentType: "cc",
          variant: "inline",
          googleFont: "",
          validationCallback: (field: string, status: boolean) => {
            const handler =
              fieldHandlersRef.current[
                field as keyof typeof fieldHandlersRef.current
              ];
            handler?.(status);

            if (status === false) {
              lastRequestIdRef.current = null;
              pendingRejectRef.current?.(new Error("Card validation failed"));
              pendingResolveRef.current = null;
              pendingRejectRef.current = null;
            }
          },
          callback: (response: any) => {
            try {
              if (response?.token) {
                const expectedId = lastRequestIdRef.current;
                const receivedId = response?.initiatedBy?.detail?.requestId;
                if (expectedId && receivedId && receivedId === expectedId) {
                  lastRequestIdRef.current = null;
                  pendingResolveRef.current?.(response.token);
                  pendingResolveRef.current = null;
                  pendingRejectRef.current = null;
                }
                return;
              }
              if (response?.error) {
                lastRequestIdRef.current = null;
                pendingRejectRef.current?.(response.error);
                pendingResolveRef.current = null;
                pendingRejectRef.current = null;
              }
            } catch (e) {
              lastRequestIdRef.current = null;
              pendingRejectRef.current?.(e);
              pendingResolveRef.current = null;
              pendingRejectRef.current = null;
            }
          },
          invalidCss: { color: "#dc3545" },
          fields: {
            ccnumber: { placeholder: "•••• •••• •••• ••••" },
            ccexp: { placeholder: "MM / YY" },
            cvv: { placeholder: "CVV" },
          },
        });

        onInit({ getToken });
      } catch {
        // init failed silently
      }
    })();

    return () => {
      cancelled = true;
      hardReset();
      lastRequestIdRef.current = null;
      pendingResolveRef.current = null;
      pendingRejectRef.current = null;
    };
  }, [disabled, publicTokenizationKey]);

  if (disabled) return null;

  return (
    <>
      <FormItem>
        <FormLabel>
          {t("models.paymentInfo.cardNumber", "Card number")}
          <span className="text-red-500 ml-1">*</span>
        </FormLabel>
        <div
          id="ccnumber"
          className={cn(
            "h-9 rounded-md border px-3 py-2 flex items-center",
            numberError ? "border-destructive" : "border-input",
          )}
        />
        {numberError && (
          <p className="text-xs font-medium text-destructive">
            {t("validation.required", "{{label}} is required", {
              label: t("models.paymentInfo.cardNumber", "Card number"),
            })}
          </p>
        )}
      </FormItem>

      <div className="grid md:grid-cols-2 gap-4">
        <FormItem>
          <FormLabel>
            {t("models.paymentInfo.dateExpiration", "Expiry date")}
            <span className="text-red-500 ml-1">*</span>
          </FormLabel>
          <div
            id="ccexp"
            className={cn(
              "h-9 rounded-md border px-3 py-2 flex items-center",
              expiryError ? "border-destructive" : "border-input",
            )}
          />
          {expiryError && (
            <p className="text-xs font-medium text-destructive">
              {t("validation.required", "{{label}} is required", {
                label: t("models.paymentInfo.dateExpiration", "Expiry date"),
              })}
            </p>
          )}
        </FormItem>

        <FormItem>
          <FormLabel>
            {t("models.paymentInfo.cvc", "CVC")}
            <span className="text-red-500 ml-1">*</span>
          </FormLabel>
          <div
            id="cvv"
            className={cn(
              "h-9 rounded-md border px-3 py-2 flex items-center",
              cvcError ? "border-destructive" : "border-input",
            )}
          />
          {cvcError && (
            <p className="text-xs font-medium text-destructive">
              {t("validation.required", "{{label}} is required", {
                label: t("models.paymentInfo.cvc", "CVC"),
              })}
            </p>
          )}
        </FormItem>
      </div>
    </>
  );
};

export default NaxellaFormFields;
